import NavIconExplore from "@/assets/images/icons/nav-icon-explore.svg";
import { Popover } from "@/components/ui/Popover";
import { useDrawer } from "@/lib/DrawerContext";
import { useIsMobile } from "@/lib/useDeviceType";
import React, { useState } from "react";
import { Platform, useWindowDimensions, View } from "react-native";
import { XStack, YStack } from "tamagui";
import { CardTitle } from "@/components/ui/Typography";
import { ExploreDropdown } from "./ExploreDropdown";

const DROPDOWN_WIDTH = 360;
const DROPDOWN_MAX_HEIGHT = 630;
/** Header + popover offset + bottom gap reserved out of the viewport height. */
const VIEWPORT_RESERVED = 120;
const DROPDOWN_BG = "rgba(13, 13, 13, 1)";
const NAV_HOVER_ORANGE = "rgba(242, 102, 58, 1)";

type ExploreMenuDropdownProps = {
  iconSlotWidth?: number;
  iconSlotHeight?: number;
};

export function ExploreMenuDropdown({
  iconSlotWidth = 26,
  iconSlotHeight = 24,
}: ExploreMenuDropdownProps = {}) {
  const isMobile = useIsMobile();
  const { openDrawer } = useDrawer();
  const { height: windowHeight } = useWindowDimensions();
  const [modalOpen, setModalOpen] = useState(false);
  const [navHover, setNavHover] = useState(false);
  const isWeb = Platform.OS === "web";
  const navAccent = isWeb && navHover ? NAV_HOVER_ORANGE : "white";
  const dropdownMaxHeight = Math.min(
    DROPDOWN_MAX_HEIGHT,
    Math.max(320, windowHeight - VIEWPORT_RESERVED),
  );

  const triggerContent = (
    <XStack
      ai="center"
      gap={6}
      cursor="pointer"
      style={isWeb ? { color: navAccent } : undefined}
    >
      <View
        style={{
          width: iconSlotWidth,
          height: iconSlotHeight,
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <NavIconExplore width={36} height={36} color={navAccent} />
      </View>
      <CardTitle
        display="none"
        $gtSm={{ display: "flex" }}
        color={navAccent}
        fontWeight="600"
        style={{ cursor: "pointer" }}
      >
        Explore
      </CardTitle>
    </XStack>
  );

  const trigger = isWeb ? (
    <div
      style={{
        borderRadius: 6,
        paddingTop: 2,
        paddingBottom: 2,
        paddingLeft: 2,
        paddingRight: 2,
        outlineWidth: 0,
        display: "flex",
        alignItems: "center",
      }}
      onMouseEnter={() => setNavHover(true)}
      onMouseLeave={() => setNavHover(false)}
    >
      {triggerContent}
    </div>
  ) : (
    <View
      style={{
        borderRadius: 6,
        paddingVertical: 2,
        paddingHorizontal: 2,
        outlineWidth: 0,
      }}
    >
      {triggerContent}
    </View>
  );

  return (
    <>
      <Popover
        open={!isMobile && modalOpen}
        onOpenChange={(open) => {
          if (open === modalOpen) return;
          setModalOpen(open);
        }}
        onMobileOpen={openDrawer}
        placement="bottom"
        offset={30}
        width={DROPDOWN_WIDTH}
        maxWidth={DROPDOWN_WIDTH}
        maxHeight={dropdownMaxHeight}
        content={
          <YStack
            width="100%"
            backgroundColor={DROPDOWN_BG}
            borderRadius="$4"
            overflow="hidden"
            shadowColor="#000"
            shadowOffset={{ width: 0, height: 4 }}
            shadowOpacity={0.25}
            shadowRadius={16}
          >
            <ExploreDropdown onNavigate={() => setModalOpen(false)} />
          </YStack>
        }
      >
        {trigger}
      </Popover>
    </>
  );
}
